/**
 * Calidad y centro de mejora.
 *
 * El feedback sobre **personas** es confidencial (§65) y por eso vive en su
 * propia pestaña, separado del feedback sobre la limpieza. Mezclarlos haría que
 * un comentario sobre alguien terminara en un reporte de satisfacción.
 */

import { useState } from 'react';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Badge, Button, Card, Cell, Empty, Note, PageHead, Table,
} from '@/components/ui';

interface Quality {
  feedback: {
    id: string; unit_code: string; category: string | null; zone: string | null;
    description: string | null; satisfaction: number | null; comment: string | null;
    submitted_by_name: string | null; created_at: string;
  }[];
  by_unit: { unit_code: string; name: string | null; avg_satisfaction: number; responses: number }[];
  product_requests: {
    id: string; product_name: string; intended_use: string | null; status: string;
    unit_code: string | null; created_at: string;
  }[];
}

function stars(n: number | null): string {
  if (n == null) return '—';
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

export function QualityPage() {
  const [tab, setTab] = useState<'cleaning' | 'staff' | 'products'>('cleaning');
  const quality = useApi<Quality>('/admin/quality');
  const staff = useApi<{ feedback: { id: string; kind: string; description: string; employee_name: string | null; unit_code: string | null; created_at: string }[] }>(
    '/admin/staff-feedback', { enabled: tab === 'staff' },
  );

  const decide = useApiMutation(
    ({ id, decision, createItem }: { id: string; decision: string; createItem: boolean }) =>
      api.post(`/admin/product-requests/${id}/decide`, { decision, create_item: createItem }),
    ['/admin/quality', '/admin/inventory'],
  );

  return (
    <>
      <PageHead title="Calidad" subtitle="Satisfacción, comentarios y sugerencias de producto." />

      <div className="tabs mb-2">
        <button type="button" className={`tab${tab === 'cleaning' ? ' is-active' : ''}`} aria-pressed={tab === 'cleaning'} onClick={() => { if (tab !== 'cleaning') { haptic.light(); setTab('cleaning'); } }}> {/* HAPTIC_FEEDBACK */}
          Limpieza
        </button>
        <button type="button" className={`tab${tab === 'staff' ? ' is-active' : ''}`} aria-pressed={tab === 'staff'} onClick={() => { if (tab !== 'staff') { haptic.light(); setTab('staff'); } }}> {/* HAPTIC_FEEDBACK */}
          Personal
        </button>
        <button type="button" className={`tab${tab === 'products' ? ' is-active' : ''}`} aria-pressed={tab === 'products'} onClick={() => { if (tab !== 'products') { haptic.light(); setTab('products'); } }}> {/* HAPTIC_FEEDBACK */}
          Productos sugeridos
        </button>
      </div>

      {tab === 'cleaning' && (
        <Async query={quality}>
          {(data) => (
            <div className="grid grid--sidebar">
              <Card title="Comentarios" flush>
                {data.feedback.length === 0 ? (
                  <Empty icon="mejora" title="Todavía no hay comentarios" />
                ) : (
                  <Table head={<tr><th>Consultorio</th><th>Comentario</th><th className="right">Satisfacción</th></tr>}>
                    {data.feedback.map((f) => (
                      <tr key={f.id}>
                        <td className="strong">
                          <Cell sub={f.created_at.slice(0, 10)}>{f.unit_code}</Cell>
                        </td>
                        <td>
                          <Cell sub={[f.category, f.zone].filter(Boolean).join(' · ')}>
                            {f.comment ?? f.description ?? '—'}
                          </Cell>
                        </td>
                        <td className="right" style={{ color: 'var(--warn)' }}>{stars(f.satisfaction)}</td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Card>

              <Card title="Promedio por consultorio" flush>
                {data.by_unit.length === 0 ? (
                  <Empty icon="calidad" title="Sin datos suficientes" />
                ) : (
                  <Table head={<tr><th>Consultorio</th><th className="right">Promedio</th></tr>}>
                    {data.by_unit.map((u) => (
                      <tr key={u.unit_code}>
                        <td><Cell sub={`${u.responses} respuestas`}>{u.unit_code}</Cell></td>
                        <td className="right">
                          <Badge tone={u.avg_satisfaction >= 4 ? 'ok' : u.avg_satisfaction >= 3 ? 'warn' : 'danger'}>
                            {u.avg_satisfaction.toFixed(1)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Card>
            </div>
          )}
        </Async>
      )}

      {tab === 'staff' && (
        <>
          <Note tone="warn">
            Esta información es confidencial. No se muestra al empleado ni a otros clientes.
          </Note>
          <Async query={staff} empty={(d) => d.feedback.length === 0 && (
            <Empty icon="personal" title="Sin reportes sobre el personal" />
          )}>
            {(data) => (
              <Card flush>
                <Table head={<tr><th>Empleado</th><th>Tipo</th><th>Detalle</th><th>Consultorio</th></tr>}>
                  {data.feedback.map((f) => (
                    <tr key={f.id}>
                      <td className="strong">{f.employee_name ?? '—'}</td>
                      <td><Badge tone={f.kind === 'RECOGNITION' ? 'ok' : 'neutral'}>{f.kind}</Badge></td>
                      <td>{f.description}</td>
                      <td className="small muted">{f.unit_code ?? '—'}</td>
                    </tr>
                  ))}
                </Table>
              </Card>
            )}
          </Async>
        </>
      )}

      {tab === 'products' && (
        <Async query={quality} empty={(d) => d.product_requests.length === 0 && (
          <Empty icon="catalogo" title="Sin productos sugeridos pendientes" />
        )}>
          {(data) => (
            <>
              <Note tone="info">
                Un producto aprobado no entra solo al inventario: se crea cuando lo decidís acá.
              </Note>
              <Card flush>
                <Table head={<tr><th>Producto</th><th>Para qué</th><th>Consultorio</th><th /></tr>}>
                  {data.product_requests.map((p) => (
                    <tr key={p.id}>
                      <td className="strong">{p.product_name}</td>
                      <td className="small muted">{p.intended_use ?? '—'}</td>
                      <td className="small">{p.unit_code ?? '—'}</td>
                      <td className="right">
                        <Button disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: p.id, decision: 'REJECTED', createItem: false })}>
                          Rechazar
                        </Button>{' '}
                        <Button variant="primary" disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: p.id, decision: 'APPROVED', createItem: true })}>
                          Aprobar y crear
                        </Button>
                      </td>
                    </tr>
                  ))}
                </Table>
              </Card>
            </>
          )}
        </Async>
      )}
    </>
  );
}
